<script setup>
import { computed, h } from 'vue'
import Button from '@components/forms/Button.vue'

const props = defineProps({
	title: {
		type: String,
		default: '',
	},
	content: {
		type: String,
		default: '',
	},
	width: {
		type: [Number, String],
		default: 100,
	},
	columns: {
		type: Array,
		default: () => [],
	},
	columnMinWidths: {
		type: Array,
		default: () => [],
	},
	borderColor: {
		type: String,
		default: '',
	},
	borderWidth: {
		type: [Number, String],
		default: 1,
	},
	imagePadding: {
		type: String,
		default: 'none',
	},
})

const imagePaddings = {
	none: '0',
	small: '0.25rem',
	medium: '0.5rem',
	large: '1rem',
}

const buttonStyles = ['solid', 'secondary']

// Editor markup
const allowedTags = ['table', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'p', 'br', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a', 'img']
const allowedAttributes = {
	col: ['style'],
	th: ['colspan', 'rowspan', 'style'],
	td: ['colspan', 'rowspan', 'style'],
	p: ['style'],
	span: ['style'],
	a: ['href', 'target', 'rel'],
	img: ['src', 'alt', 'width', 'height', 'style'],
}

const columnWidths = computed(() => props.columns.map((column) => Number(column)).filter((column) => column > 0 && column <= 100))

const columnMinWidths = computed(() =>
	props.columnMinWidths.map((column) => {
		const minWidth = Number(column)
		return minWidth > 0 && minWidth <= 2000 ? minWidth : null
	}),
)

const scrollsOnMobile = computed(() => columnMinWidths.value.some((minWidth) => minWidth !== null))

const tableMinWidth = computed(() => columnMinWidths.value.reduce((total, minWidth) => total + (minWidth ?? 0), 0))

const tableWidth = computed(() => {
	const width = Number(props.width)
	return width > 0 && width <= 100 ? width : 100
})

const styles = computed(() => ({
	width: `${tableWidth.value}%`,
	'--content-table-border-color': props.borderColor || null,
	'--content-table-border-width': `${Math.max(0, Number(props.borderWidth) || 0)}px`,
	'--content-table-image-padding': imagePaddings[props.imagePadding] ?? imagePaddings.none,
	'--content-table-min-width': scrollsOnMobile.value ? `${tableMinWidth.value}px` : null,
}))

const safeHref = (href) => (/^(?:https?:|mailto:|tel:|\/|#)/i.test((href ?? '').trim()) ? href.trim() : null)

const attributesFor = (element) => {
	const tag = element.tagName.toLowerCase()
	const allowed = allowedAttributes[tag] ?? []

	return Object.fromEntries(allowed.map((name) => [name, element.getAttribute(name)]).filter(([, value]) => value !== null))
}

const toVNodes = (parent) =>
	[...parent.childNodes]
		.map((node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				return node.textContent
			}

			if (node.nodeType !== Node.ELEMENT_NODE) {
				return null
			}

			const tag = node.tagName.toLowerCase()

			if (!allowedTags.includes(tag)) {
				return null
			}

			const children = toVNodes(node)

			// Buttons
			if (tag === 'a') {
				const href = safeHref(node.getAttribute('href'))
				const style = (node.getAttribute('data-button') ?? '').trim().toLowerCase()
				const isFullWidth = (node.getAttribute('data-button-width') ?? '').trim().toLowerCase() === 'full'

				if (href && buttonStyles.includes(style)) {
					return h(Button, { href, target: node.getAttribute('target') ?? '_blank', class: [style, 'thin', { wide: isFullWidth }] }, () => toVNodes(node))
				}

				return href ? h('a', { ...attributesFor(node), href }, children) : children
			}

			return h(tag, attributesFor(node), children.length ? children : null)
		})
		.filter((node) => node !== null)

// Table markup
const tree = computed(() => {
	if (!props.content) {
		return null
	}

	const parsed = new DOMParser().parseFromString(props.content, 'text/html')

	parsed.querySelectorAll('table').forEach((table) => {
		table.querySelector('colgroup')?.remove()
		table.removeAttribute('style')

		const columnCount = Math.max(columnWidths.value.length, columnMinWidths.value.length)

		if (columnCount === 0) {
			return
		}

		const colgroup = parsed.createElement('colgroup')

		for (let column = 0; column < columnCount; column++) {
			const col = parsed.createElement('col')
			const columnWidth = columnWidths.value[column]
			const minWidth = columnMinWidths.value[column]

			if (columnWidth) {
				col.style.width = `${columnWidth}%`
			}

			if (minWidth) {
				col.style.setProperty('--content-table-col-min-width', `${minWidth}px`)
			}

			colgroup.append(col)
		}

		table.prepend(colgroup)
	})

	return () => toVNodes(parsed.body)
})
</script>

<template>
	<div class="my-2 flex max-w-full min-w-0 flex-col">
		<h3 class="content-table-title" v-if="title">{{ title }}</h3>
		<div
			:class="['content-table', { 'content-table-fixed': columnWidths.length, 'content-table-scrolls': scrollsOnMobile }]"
			:style="styles"
			:tabindex="scrollsOnMobile ? 0 : null"
			:role="scrollsOnMobile ? 'region' : null"
			:aria-label="scrollsOnMobile ? `${title || 'Table'}, scrolls sideways` : null"
		>
			<component :is="tree" v-if="tree" />
		</div>
	</div>
</template>
